"use client";
import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { api } from "@/lib/api";
import { usePendingPoll } from "@/lib/usePendingPoll";
import { Toast, SpinnerIcon, WarningIcon } from "@/components/Toast";
import {
  getPendingEval, setPendingEval, clearPendingEval, PENDING_EVAL_EVENT, type PendingEval,
} from "@/lib/pendingEval";

// Ending a session drops the student straight back on the home page, so THIS is
// what tells them what happened to their supervisor report. Two beats, both as a
// top-right toast:
//   1. "running"  : the supervisor is writing the report (shown the moment they land)
//   2. "ready"    : it's done, with a button that opens the report
// plus two terminal-but-quiet cases: "failed" (retry) and "too_short" (nothing to
// supervise). Dismissing the running toast only closes THAT beat; the ready toast
// still arrives, which is the whole point of leaving the session screen.
//
// Deliberately no global state library: `usePendingPoll` + local state, mounted
// by the shell layout so it follows the student across every page of the app.
// The poller, the toast chrome and the countdown live in lib/usePendingPoll and
// components/Toast (F6); this file is the content.

type Kind = "running" | "ready" | "failed" | "too_short";

// How long each beat stays on screen before closing itself. "ready" gets the
// longest run because it is the one beat with something to click; the others are
// pure information and can leave sooner. null = no countdown at all: "failed" is
// the student's only handle on a report that did not happen, so it waits for
// them, retry or close, in their own time.
const AUTO_DISMISS_MS: Record<Kind, number | null> = {
  running: 8000,
  ready: 12000,
  failed: null,
  too_short: 10000,
};
const TERMINAL: readonly Kind[] = ["failed", "too_short"];
// Message keys per beat: <prefix>Title / <prefix>Body / <prefix>BodyWith.
const PREFIX: Record<Kind, string> = {
  running: "running", ready: "ready", failed: "failed", too_short: "tooShort",
};

const identity = (e: PendingEval) => e.sessionId;
const isReady = (e: PendingEval) => !!e.ready;
const clearAll = () => clearPendingEval();
const EVENTS = [PENDING_EVAL_EVENT] as const;

export function PendingEvalWatcher() {
  const t = useTranslations("reportToast");
  const locale = useLocale();
  const router = useRouter();

  const { entry, kind, visible, dismissBeat, end } = usePendingPoll<PendingEval, Kind>({
    read: getPendingEval,
    identity,
    isReady,
    readyBeat: "ready",
    runningBeat: "running",
    terminalBeats: TERMINAL,
    clear: clearAll,
    events: EVENTS,
    deps: [locale],
    poll: async (pending) => {
      const { status } = await api.getEvaluationStatus(pending.sessionId);
      if (status === "ready") {
        // Kept in localStorage on purpose: the report link is worth holding onto
        // until the student acknowledges it (opening the report clears it too).
        setPendingEval({ ...pending, ready: true });
        return { beat: "ready" };
      }
      if (status === "failed") {
        clearPendingEval(pending.sessionId);
        return { beat: "failed" };
      }
      if (status === "too_short") {
        // No report is coming and retrying can't change that: say it once.
        clearPendingEval(pending.sessionId);
        return { beat: "too_short" };
      }
      if (status === "none") {
        // Job lost (server restart mid-run): re-trigger it and keep waiting.
        await api.evaluateSession(pending.sessionId, locale);
      }
      return { again: true };
    },
  });

  if (!visible || !entry || !kind) return null;

  const reportHref = (() => {
    const params = new URLSearchParams();
    if (entry.caseId) params.set("case", entry.caseId);
    if (entry.patient) params.set("patient", entry.patient);
    const qs = params.toString();
    return `/report/${entry.sessionId}${qs ? `?${qs}` : ""}`;
  })();

  function openReport() {
    end();
    router.push(reportHref);
  }

  const key = PREFIX[kind];
  const body = entry.patient ? t(`${key}BodyWith`, { name: entry.patient }) : t(`${key}Body`);
  // Only the two actionable beats carry a button.
  const cta = kind === "ready" ? t("readyCta") : kind === "failed" ? t("failedCta") : null;

  const icon = kind === "running" ? <SpinnerIcon /> : kind === "ready" ? (
    <svg aria-hidden className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 8.25h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  ) : kind === "too_short" ? (
    <svg aria-hidden className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm3.75 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm3.75 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
    </svg>
  ) : <WarningIcon />;

  return (
    <Toast
      position="top"
      beatKey={kind}
      // Ready is the only celebratory beat; running/too_short are neutral, failed warns.
      tone={kind === "failed" ? "warn" : "accent"}
      icon={icon}
      title={t(`${key}Title`)}
      body={body}
      cta={cta}
      onCta={openReport}
      autoDismissMs={AUTO_DISMISS_MS[kind]}
      onClose={dismissBeat}
      dismissLabel={t("dismiss")}
      autoCloseLabel={(seconds) => t("autoClose", { seconds })}
    />
  );
}
